import React from 'react';
import { ColumnInstance } from 'react-table';
import { useTranslation } from '@fluentsms/i18n';
import { FilterTypeSelector } from './FilterTypeSelector';
import DataEmptyFilter from './DataEmptyFilter';
import { createStyles, Grid, makeStyles, MenuItem, TextField, Theme } from '@material-ui/core';
import { Filter } from '../DataGridFilterMenu';
import { FilterColumns } from './types/filterTypes';
export type EnumFilterableColumnInstance = ColumnInstance & {
  isEnumFilterColumn?: boolean;
  enumFilterOptions?: string[];
  accessor: (row: Record<string, unknown>) => string;
};

export type MultiSelectFilterableColumnInstance = ColumnInstance & {
  isMultiselectColumn?: boolean;
  multiselectFilterOptions?: string[];
  enableSelectAll?: boolean;
  hideAllOperatorOption?: boolean;
  accessor: (row: Record<string, unknown>) => string;
};

export type DataFilterRowPropTypes<D extends Record<string, unknown>> = {
  columns: FilterColumns[];
  filter?: Filter;
  onFilterChange?: (filterId: string, value: Filter) => void;
  onColumnChange?: (oldFilterId: string, newColumnId: string) => void;
  filterId: string;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    columnSelectLabel: {
      textTransform: 'capitalize',
    },
    columnSelect: {
      width: '100%',
    },
    filterRowContainer: {
      paddingBottom: theme.spacing(2),
    },
  }),
);

const findActiveColumn = (columns: FilterColumns[], columnId?: string | null): FilterColumns | undefined => {
  return columns?.find((column) => {
    return column.id === columnId;
  });
};

export function DataFilterRow<T extends Record<string, unknown>>({
  columns,
  onFilterChange,
  onColumnChange,
  filter,
  filterId,
}: DataFilterRowPropTypes<T>): JSX.Element {
  const { t } = useTranslation('componentLibrary');
  const chosenColumn = findActiveColumn(columns, filter?.columnId);
  const classes = useStyles();
  const handleColumnChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    onColumnChange && onColumnChange(filterId, event.target.value as string);
  };
  const handleFilterChange = (filterValue: Filter) => {
    onFilterChange && onFilterChange(filterId, filterValue);
  };

  const handleFilterChanges = (filterValue: Filter) => {
    const optionsSelected = Array.isArray(filterValue.value)
      ? filterValue.value
      : filterValue?.value?.toString()?.split(',');
    onFilterChange && onFilterChange(filterId, { ...filterValue, value: optionsSelected ?? null });
  };

  let columnOptions: string[] = [];
  const isEnumFilterColumn = chosenColumn?.isEnumFilterColumn ?? false;
  const enumFilterOptions = chosenColumn?.enumFilterOptions;
  const isMultiselectFilterColumn = chosenColumn?.isMultiselectColumn ?? false;
  const multiSelectFilterOptions = chosenColumn?.multiselectFilterOptions;
  const multiSelectEnableSelectAll = chosenColumn?.enableSelectAll;
  const hideAllOperatorOption = chosenColumn?.hideAllOperatorOption;
  const selectHandlerFilter = isMultiselectFilterColumn ? handleFilterChanges : handleFilterChange;

  if (isEnumFilterColumn && enumFilterOptions) {
    // enum filter column with options provided
    columnOptions = enumFilterOptions;
  } else if (isMultiselectFilterColumn && multiSelectFilterOptions) {
    columnOptions = multiSelectFilterOptions;
  }

  return (
    <Grid container className={classes.filterRowContainer}>
      {/* Render <select> menu of all available table columns */}
      <Grid item xs={12} sm={4}>
        <TextField
          id={`column-select-for-${filterId}`}
          value={filter?.columnId || ''}
          onChange={handleColumnChange}
          label={t('column')}
          InputLabelProps={{
            className: classes.columnSelectLabel,
          }}
          className={classes.columnSelect}
          data-testid="data-filter-row-select"
          select
        >
          {columns?.map((column) => (
            <MenuItem key={`filter-column-${column.id}`} value={column.id} data-testid="data-filter-row-select-option">
              {column.Header}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      {/* Render the filter associated with that column */}
      <Grid item xs={12} sm={8} data-testid="data-filter-row-filter-component">
        {chosenColumn && filter && chosenColumn.filterType && (
          <FilterTypeSelector
            filter={filter}
            onFilterChange={selectHandlerFilter}
            filterType={chosenColumn.filterType}
            columns={columns}
            columnOptions={columnOptions}
            enableSelectAll={multiSelectEnableSelectAll}
            hideAllOperatorOption={hideAllOperatorOption}
            selectAllLabel={filter?.columnId ?? undefined}
          />
        )}
        {(!filter?.columnId || filter?.columnId === 'null') && <DataEmptyFilter />}
      </Grid>
    </Grid>
  );
}

export default DataFilterRow;
